import { roundValuesInObject } from "../utilities";

/**
 * @constant
 * @type {number}
 * @default
 */
const ANNUAL_NUMBER_OF_MONTHS = 12;

/**
 * @constant
 * @type {number}
 * @default
 */
const ASSUMED_INFLATION_RATE = 0.02;

const FREQ_MAP_MULTIPLIERS = {
    weekly: 52 / 12,
    everyTwoWeeks: 26 / 12,
    twiceAMonth: 24 / 12,
    monthly: 1
};

const FREQ_MAP_DIVISORS = {
    everyTwoMonths: 2,
    quarterly: 3,
    everyFourMonths: 4,
    semiAnnually: 6,
    annually: 12
};

/**
 * @constant
 * @type {string}
 * @default
 */
const lang = document.documentElement.lang.toLowerCase().indexOf("fr") >= 0 ? "fr" : "en";

/**
 * Calculate the cash flow based on retirement savings and post
 * retirement return rate over a period.
 * @param {Object} parameters
 * @param {number} parameters.retirementSavings
 * @param {number} parameters.period
 * @return {number} cash flow
 */
function calculateCashFlow({ retirementSavings, returnRate, period }) {
    const effectiveGrowthRate = (1 + returnRate) / (1 + ASSUMED_INFLATION_RATE) - 1;
    const nominalGrowthRate =
        ((1 + effectiveGrowthRate) ** (1 / ANNUAL_NUMBER_OF_MONTHS) - 1) * ANNUAL_NUMBER_OF_MONTHS;
    const monthlyNominalGrowthRate = nominalGrowthRate / ANNUAL_NUMBER_OF_MONTHS;
    const term = (1 + monthlyNominalGrowthRate) ** (ANNUAL_NUMBER_OF_MONTHS * period);

    return monthlyNominalGrowthRate === 0
        ? retirementSavings / (ANNUAL_NUMBER_OF_MONTHS * period)
        : (retirementSavings * monthlyNominalGrowthRate) / (1 - 1 / term) / (1 + monthlyNominalGrowthRate);
}

function getLegendNumbers() {
    return lang === "en"
        ? ["Current/space/savings", "Current/space/savings\n + PAC", "Current/space/savings\n + PAC\n + increase"]
        : [
              "Épargne/space/actuelle",
              "Épargne/space/actuelle\n + PAC",
              "Épargne/space/actuelle\n + PAC\n + augmentation"
          ];
}

/**
 * Calculate interval based on period.
 * @param {number} period
 * @return {number} interval
 */
function calculateInterval(period) {
    let number = period;

    while (true) {
        if (number < 5) {
            return 1;
        } else if (number % 3 === 0) {
            return number / 3;
        } else if (number % 4 === 0) {
            return number / 4;
        } else if (number % 5 === 0) {
            return number / 5;
        }

        number -= 1;
    }
}

/**
 * Calculate savings after a fixed interval of years.
 * @param {Object} parameters
 * @param {number} parameters.startingAmount
 * @param {number} parameters.contributionAmount
 * @param {string} parameters.contributionFrequency
 * @param {number} parameters.numberYears
 * @param {number} parameters.portfolioType
 * @param {number} parameters.annualIncreasePercent
 * @param {number} parameters.annualIncreaseDollar
 * @return {Object} savings
 */
function calculateSavings({
    startingAmount,
    contributionAmount,
    contributionFrequency,
    numberYears,
    portfolioType,
    annualIncreasePercent,
    annualIncreaseDollar,
    annualIncreaseType
}) {
    const savingsPeriod = numberYears;
    const monthlyReturn = Math.pow(1 + portfolioType, 1 / 12) - 1;
    // const effectiveContributionRate = (1 + portfolioType) ** (1 / contributionFrequency);
    const interval = calculateInterval(savingsPeriod);
    const savings = {};
    let openingBalance = startingAmount;

    for (let year = 1; year <= savingsPeriod; year++) {
        let finalBalance = openingBalance;
        for (let month = 1; month <= 12; month++) {
            let monthDeposits = 0;
            if (contributionAmount > 0) {
                const shouldMultiply = Object.keys(FREQ_MAP_MULTIPLIERS).includes(contributionFrequency);
                const factor = shouldMultiply ? FREQ_MAP_MULTIPLIERS[contributionFrequency] : 1;
                const divisor = FREQ_MAP_DIVISORS[contributionFrequency];

                const monthlyContribution = contributionAmount * factor;

                const shouldAccumulate = shouldMultiply || (!shouldMultiply && month % divisor === 0);

                if (shouldAccumulate) {
                    monthDeposits += monthlyContribution;

                    let annualIncrease = 0;
                    if (annualIncreaseType === "percent" && annualIncreasePercent > 0) {
                        annualIncrease = Math.pow(1 + annualIncreasePercent, year - 1);
                        monthDeposits *= annualIncrease;
                    } else if (annualIncreaseType === "dollar" && annualIncreaseDollar > 0) {
                        annualIncrease = annualIncreaseDollar * (year - 1);
                        const monthlyIncrease = annualIncrease * factor;
                        monthDeposits += monthlyIncrease;
                    }
                }
            }

            const monthInterest = openingBalance * monthlyReturn;
            finalBalance += monthDeposits + monthInterest;
            openingBalance = finalBalance;
        }

        if (year % interval === 0) {
            savings[`${year}years`] = finalBalance;
        }

        if (year === savingsPeriod) {
            savings.retirement = finalBalance;
        }
    }
    return savings;
}

/**
 * Calculate monthly withdrawals after retirement that would deplete
 * savings to 0 over 10, 20 and 30 years.
 * @param {Object} parameters
 * @param {number} parameters.retirementSavings
 * @param {number} parameters.returnRate
 * @return {Object} withdrawals
 */
function calculateWithdrawals({ retirementSavings, returnRate }) {
    return {
        "10years": calculateCashFlow({
            retirementSavings,
            returnRate,
            period: 10
        }),
        "20years": calculateCashFlow({
            retirementSavings,
            returnRate,
            period: 20
        }),
        "30years": calculateCashFlow({
            retirementSavings,
            returnRate,
            period: 30
        })
    };
}

/**
 * Calculate savings and withdrawals results.
 * @param {Object} inputValues
 * @return {Object} results
 */
function calculateResults(inputValues) {
    // Savings without PAC and with annual increase
    const noPACSavings = calculateSavings({
        ...inputValues,
        contributionAmount: 0,
        annualIncreasePercent: 0,
        annualIncreaseDollar: 0,
        annualIncreaseType: null
    });
    // Savings with PAC and without annual increase
    const noAnnualIncreaseSavings = calculateSavings({
        ...inputValues,
        annualIncreasePercent: 0,
        annualIncreaseDollar: 0,
        annualIncreaseType: null
    });
    // Savings with PAC and annual increase
    const pacSavings = calculateSavings(inputValues);

    // Withdrawals without PAC and with annual increase
    const noPACWithdrawals = calculateWithdrawals({
        retirementSavings: noPACSavings.retirement,
        returnRate: inputValues.interestRateCashFlow
    });
    // Withdrawals with PAC and without annual increase
    const noAnnualIncreaseWithdrawals = calculateWithdrawals({
        retirementSavings: noAnnualIncreaseSavings.retirement,
        returnRate: inputValues.interestRateCashFlow
    });
    // Withdrawals with PAC and annual increase
    const pacWithdrawals = calculateWithdrawals({
        retirementSavings: pacSavings.retirement,
        returnRate: inputValues.interestRateCashFlow
    });

    const legendValues = getLegendNumbers();

    return {
        savings: {
            noPAC: roundValuesInObject(noPACSavings),
            noAnnualIncrease: roundValuesInObject(noAnnualIncreaseSavings),
            pac: roundValuesInObject(pacSavings),
            years: inputValues.numberYears,
            legend: legendValues,
            startingAmount: inputValues.startingAmount,
            increase: {
                percent: inputValues.annualIncreasePercent,
                dollars: inputValues.annualIncreaseDollar,
                type: inputValues.annualIncreaseType
            }
        },
        withdrawals: {
            // Withdrawals without PAC and with annual increase
            noPAC: roundValuesInObject(noPACWithdrawals),
            // Withdrawals with PAC and without annual increase
            noAnnualIncrease: roundValuesInObject(noAnnualIncreaseWithdrawals),
            // Withdrawals with PAC and annual increase
            pac: roundValuesInObject(pacWithdrawals),
            legend: legendValues
        }
    };
}

export { calculateResults };
